import { ApiError } from '@fc/core'
import { Button, Card, CardContent, Input, Label } from '@fc/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { createResource, type NewResourceInput } from '../../lib/resources-api'

/** True when the API rejected the create because the name/slug is taken. */
function isDuplicateSlugError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 400 && Boolean(err.fields?.slug)
}

const KINDS: { value: NonNullable<NewResourceInput['kind']>; label: string }[] = [
  { value: 'room', label: 'Room' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'other', label: 'Other' },
]

const fieldClass =
  'flex h-10 w-full rounded-md border border-border bg-bg-alt px-3 py-2 text-sm text-fg ' +
  'focus-visible:outline-none focus-visible:border-secondary focus-visible:ring-[3px] focus-visible:ring-ring'

export function ResourceNew() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [kind, setKind] = useState<NonNullable<NewResourceInput['kind']>>('room')
  const [location, setLocation] = useState('')
  const [capacity, setCapacity] = useState('')
  const [description, setDescription] = useState('')

  const mutation = useMutation({
    mutationFn: (input: NewResourceInput) => createResource(input),
    onSuccess: (resource) => {
      queryClient.invalidateQueries({ queryKey: ['resources'] })
      navigate(`/app/resources/${resource.id}`)
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    mutation.mutate({
      name: name.trim(),
      kind,
      location: location.trim() || null,
      capacity: capacity ? Number(capacity) : null,
      description: description.trim() || null,
    })
  }

  const duplicateSlug = mutation.isError && isDuplicateSlugError(mutation.error)

  return (
    <div className="space-y-6">
      <PageHeader
        title="New resource"
        description="Create a bookable room, equipment, or vehicle."
        actions={
          <Button asChild variant="ghost" size="sm">
            <Link to="/app/resources">
              <ArrowLeft />
              Back
            </Link>
          </Button>
        }
      />

      <Card className="max-w-2xl">
        <CardContent className="pt-5">
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="resource-name">Name</Label>
              <Input
                id="resource-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Fellowship Hall"
                required
                autoFocus
                aria-invalid={duplicateSlug || undefined}
                aria-describedby={duplicateSlug ? 'resource-name-error' : undefined}
              />
              {duplicateSlug && (
                <p id="resource-name-error" className="text-sm text-danger" role="alert">
                  A resource with this name already exists. Choose a different name.
                </p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="resource-kind">Kind</Label>
                <select
                  id="resource-kind"
                  className={fieldClass}
                  value={kind}
                  onChange={(e) => setKind(e.target.value as NonNullable<NewResourceInput['kind']>)}
                >
                  {KINDS.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="resource-capacity">Capacity</Label>
                <Input
                  id="resource-capacity"
                  type="number"
                  min={0}
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="resource-location">Location</Label>
              <Input
                id="resource-location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Main building, 2nd floor"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="resource-description">Description</Label>
              <textarea
                id="resource-description"
                className={`${fieldClass} h-24 resize-y py-2`}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional notes about this resource."
              />
            </div>

            {mutation.isError && !duplicateSlug && (
              <p className="text-sm text-danger" role="alert">
                {mutation.error instanceof Error
                  ? mutation.error.message
                  : 'Could not create the resource.'}
              </p>
            )}

            <div className="flex items-center gap-3">
              <Button type="submit" disabled={mutation.isPending || !name.trim()}>
                {mutation.isPending ? 'Creating…' : 'Create resource'}
              </Button>
              <Button asChild variant="ghost" type="button">
                <Link to="/app/resources">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
